import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, Wrench } from "lucide-react";
import { Button } from "./ui/button";
import { BUSINESS } from "../mock";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top bar */}
      <div className="hidden md:block bg-zinc-900 text-zinc-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-10 text-xs tracking-wide">
          <div className="flex items-center gap-6">
            <a href={BUSINESS.phoneHref} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="topbar-phone">
              <Phone size={13} className="text-red-500" />
              {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="topbar-email">
              <Mail size={13} className="text-red-500" />
              {BUSINESS.email}
            </a>
          </div>
          <a href={BUSINESS.mapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="topbar-address">
            <MapPin size={13} className="text-red-500" />
            {BUSINESS.address}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className={`bg-white/95 backdrop-blur-md transition-shadow duration-300 ${scrolled ? "shadow-md" : "border-b border-zinc-100"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
            <span className="flex items-center justify-center w-10 h-10 rounded-md bg-red-600 text-white transition-transform duration-300 group-hover:rotate-12">
              <Wrench size={20} />
            </span>
            <span className="leading-none">
              <span className="block font-heading text-2xl font-bold tracking-tight text-zinc-900">
                Caliber<span className="text-red-600">AUTO</span>
              </span>
              <span className="block text-[10px] font-semibold tracking-[0.22em] text-zinc-500 uppercase mt-0.5">
                Level Above The Class
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide uppercase transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:bg-red-600 after:transition-all after:duration-300 ${
                    isActive ? "text-red-600 after:w-full" : "text-zinc-700 hover:text-red-600 after:w-0 hover:after:w-full"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              asChild
              className="hidden sm:inline-flex bg-red-600 hover:bg-red-700 text-white font-semibold tracking-wide uppercase text-xs px-6 h-11 rounded-md shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              data-testid="header-appointment-btn"
            >
              <Link to="/appointment">Make an Appointment</Link>
            </Button>
            <button
              className="lg:hidden p-2 text-zinc-800 hover:text-red-600 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-zinc-100 shadow-lg" data-testid="mobile-menu">
            <nav className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors ${
                      isActive ? "text-red-600 bg-red-50" : "text-zinc-700 hover:bg-zinc-50"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Button asChild className="mt-3 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase text-xs h-11">
                <Link to="/appointment">Make an Appointment</Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
